// src/App.js
import React, { useState } from 'react';
import EssentialEightAssessment from './components/EssentialEightAssessment';
import { useModuleAccess } from './hooks/useModuleAccess';
import { AIAnalysisProvider } from './components/AIAnalysisHelper'; 
import { SidebarProvider } from './contexts/SidebarContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppStateProvider, useAppStateDebug } from './contexts/AppStateContext';
import { OrganisationGate, OrganisationProvider } from './contexts/OrganisationContext';
import { ModuleAccessProvider } from './contexts/ModuleAccessContext';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import PendingApprovalScreen from './components/Auth/PendingApprovalScreen';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import AuthCallback from './components/Auth/AuthCallback';
import AppWithMFA from './components/Auth/AppWithMFA';
import SessionStatus from './components/SessionStatus';
import { retireLegacyConnectSecureStorage } from './services/retireLegacyConnectSecureStorage';
import './index.css';
import { Toaster } from 'react-hot-toast';

export function CapabilityGate({ children }) {
  const { userModules, loading, error } = useModuleAccess();
  if (loading) return <div className="p-8 text-center">Loading your permissions...</div>;
  if (error) return <div className="p-8 text-center" role="alert">Access denied</div>;
  if (!userModules?.length) return <div className="p-8 text-center" role="alert">No modules assigned</div>;
  return children;
}

function AuthenticatedApp() {
  const { user, userProfile, loading, isAdmin, isApproved, isPending, ensureUserProfile } = useAuth();
  const [authMode, setAuthMode] = useState('login'); // 'login', 'signup', 'forgot-password'
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Enable global app state debugging
  useAppStateDebug();

  const toggleAuthMode = () => {
    setAuthMode(authMode === 'login' ? 'signup' : 'login');
  };

  const handleForgotPassword = () => {
    setAuthMode('forgot-password');
  };

  const handleBackToLogin = () => {
    // Clear URL hash/params to exit callback mode
    if (window.history.replaceState) {
      window.history.replaceState(null, null, '/');
    }
    setAuthMode('login');
  };

  // Smart profile loading - load profile when user needs access to the main app
  React.useEffect(() => {
    if (user && !userProfile && !profileLoading && !loading) {
      console.log('🔍 App: User authenticated but no profile - loading profile for access check...');
      setProfileLoading(true);
      ensureUserProfile().finally(() => {
        setProfileLoading(false);
      });
    }
  }, [user, userProfile, profileLoading, loading, ensureUserProfile]);

  // Check if user came from password reset link or email confirmation callback
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlHash = window.location.hash;
    const isPasswordReset = urlParams.get('type') === 'recovery' || 
                           urlHash.includes('type=recovery') ||
                           window.location.pathname === '/reset-password';
    
    console.log('🔍 Checking for password reset:', {
      url: window.location.href,
      search: window.location.search,
      hash: window.location.hash,
      type: urlParams.get('type'),
      isPasswordReset,
      user: user?.email
    });
    
    if (isPasswordReset && user) {
      console.log('🔍 Password reset detected - showing reset form');
      setShowPasswordReset(true);
    }
  }, [user]);

  // Check if this is an email confirmation callback
  const isAuthCallback = window.location.pathname === '/auth/callback' ||
                         window.location.hash.includes('access_token') ||
                         window.location.hash.includes('type=signup') ||
                         (window.location.search.includes('type=signup'));

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-600">
            {loading ? 'Loading...' : 'Checking account status...'}
          </p>
        </div>
      </div>
    );
  }

  // Check for email confirmation callback first (before authentication checks)
  if (isAuthCallback) {
    console.log('🔗 Auth callback detected - showing confirmation screen');
    return <AuthCallback onBackToLogin={handleBackToLogin} />;
  }

  // Not authenticated - show login/signup/forgot password
  if (!user) {
    if (authMode === 'login') {
      return <Login onToggleMode={toggleAuthMode} onForgotPassword={handleForgotPassword} />;
    } else if (authMode === 'signup') {
      return <Signup onToggleMode={toggleAuthMode} />;
    } else if (authMode === 'forgot-password') {
      return <ForgotPassword onBack={handleBackToLogin} />;
    }
  }

  // Show password reset form if user came from reset link
  if (user && showPasswordReset) {
    return <ResetPassword onComplete={() => setShowPasswordReset(false)} />;
  }

  // User is authenticated but waiting for email confirmation (not admin approval)
  if (user && isPending && !isAdmin) {
    return <PendingApprovalScreen />;
  }

  // User is authenticated and email confirmed (automatic approval) or is admin
  // Enforce module permission presence for non-admins
  if (user && (isApproved || isAdmin)) {
    return (
      <AppWithMFA>
        <OrganisationProvider>
          <OrganisationGate>
            <ModuleAccessProvider>
              <CapabilityGate>
                <SessionStatus />
                <SidebarProvider>
                  <AIAnalysisProvider>
                    <div className="App"><EssentialEightAssessment /></div>
                  </AIAnalysisProvider>
                </SidebarProvider>
              </CapabilityGate>
            </ModuleAccessProvider>
          </OrganisationGate>
        </OrganisationProvider>
      </AppWithMFA>
    );
  }

  // Fallback for any other states - likely a user without email confirmation
  if (user && !isApproved) {
    return <PendingApprovalScreen />;
  }

  // Final fallback
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="text-center">
        <p className="text-red-600">Access denied. Please contact an administrator.</p>
      </div>
    </div>
  );
}

function App() {
  React.useEffect(() => {
    retireLegacyConnectSecureStorage();
  }, []);

  return (
    <>
      <Toaster position="top-right" />
      <AuthProvider>
        <AppStateProvider>
          <AuthenticatedApp />
        </AppStateProvider>
      </AuthProvider>
    </>
  );
}

export default App;
